import { acceptHMRUpdate, defineStore } from "pinia";
import { reactive, toRefs } from "vue";

import { Cancellation } from "@/api";
import { useSingleton } from "@/composables";
import { StoresHelper } from "@/helpers";
import { DictionaryGroupsService } from "@/services";
import type { DictionaryGroup } from "@/types";

interface State {
  status: "initial" | "loading" | "loaded" | "failed";
  groups: DictionaryGroup[];
}

const getDictionaryGroupsSingleton = useSingleton(DictionaryGroupsService.getDictionaryGroups);

export const useDictionaryGroupsStore = defineStore(
  "dictionaryGroups",
  () => {
    const state = reactive<State>({
      status: "initial",
      groups: []
    });

    const fetch = async () => {
      const prevStatus = state.status;

      try {
        state.status = "loading";
        state.groups = await getDictionaryGroupsSingleton();
        state.status = "loaded";
      } catch (e) {
        if (e instanceof Cancellation) {
          state.status = prevStatus;
          return;
        }

        state.status = "failed";
        throw e;
      }
    };

    return {
      ...toRefs(state),
      fetch
    };
  },
  {
    persist: {
      pick: StoresHelper.validatePaths<State>(["groups"])
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDictionaryGroupsStore, import.meta.hot));
}
